"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

export default function FinalCTASection() {
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.15 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="py-32 relative overflow-hidden"
      style={{ backgroundColor: "#1F1F1F" }}
    >
      {/* Background element */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(63,90,69,0.15) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-container mx-auto px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div
            ref={(el) => { refs.current[0] = el; }}
            className="reveal"
          >
            <span
              className="text-xs font-semibold uppercase tracking-widest mb-6 block"
              style={{ color: "rgba(63,90,69,0.8)", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              One Decision. One Product.
            </span>
          </div>

          <div
            ref={(el) => { refs.current[1] = el; }}
            className="reveal stagger-1"
          >
            <h2
              className="font-serif font-bold text-white mb-6"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
                letterSpacing: "-0.03em",
                lineHeight: "1.0",
              }}
            >
              Ready to Simplify
              <br />
              <em style={{ color: "#3F5A45" }}>Your Rotation?</em>
            </h2>
          </div>

          <div
            ref={(el) => { refs.current[2] = el; }}
            className="reveal stagger-2"
          >
            <p
              className="text-lg leading-relaxed mb-10"
              style={{
                color: "rgba(255,255,255,0.55)",
                fontFamily: "Inter, system-ui, sans-serif",
                maxWidth: "40ch",
                margin: "0 auto 2.5rem",
              }}
            >
              One product. Three lines. Lab-verified. 30-day guarantee. Free sample with every order.
            </p>
          </div>

          <div
            ref={(el) => { refs.current[3] = el; }}
            className="reveal stagger-3 flex flex-wrap gap-4 justify-center mb-10"
          >
            <Link
              href="/shop"
              className="btn-primary text-sm"
              style={{ backgroundColor: "#3F5A45", padding: "1rem 2.5rem" }}
            >
              Shop 1 Kilo
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <Link
              href="/lab-testing"
              className="btn-secondary text-sm"
              style={{ borderColor: "rgba(255,255,255,0.2)", color: "white", padding: "1rem 2rem" }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "rgba(255,255,255,0.08)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "transparent";
              }}
            >
              View Lab Results
            </Link>
          </div>

          {/* Trust row */}
          <div
            ref={(el) => { refs.current[4] = el; }}
            className="reveal stagger-4 flex flex-wrap justify-center items-center gap-x-6 gap-y-2"
          >
            {["Free Sample Included", "30-Day Guarantee", "Third-Party Tested", "Ships in 24 Hours"].map((item, i) => (
              <span
                key={i}
                className="flex items-center gap-2 text-xs"
                style={{ color: "rgba(255,255,255,0.35)", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke="#3F5A45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}